import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";

import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { Period } from "@/shared/domain/metrics";
import { useInfiniteQueryMetrics } from "@/shared/domain/metrics/hooks";
import { Button } from "@/shared/foundation/primitives/Button";
import { Goto } from "@/shared/navigation/Goto";
import { nsToIso } from "@/shared/time/time";

import {
  DASHBOARD_PARAMS,
  readRange,
} from "../DashboardHeader/DashboardParams";

import { BucketWindow, bucketToQueryRequest } from "./bucketWindow";
import styles from "./DrillDownDialog.module.css";
import { getDrillDownTitle } from "./drillDownTitle";

export interface DrillDownDialogProps {
  open: boolean;
  onClose: () => void;
  metricName: string;
  period: Period;
  /** The clicked chart bucket, or `null` when nothing is selected. */
  bucket: BucketWindow | null;
  orgId: string | undefined;
  deviceIds?: string[];
  sessionIds?: string[];
}

/**
 * Modal that lists the raw metric records that fall within a single clicked
 * aggregate bucket. The query is gated on `open` so nothing is fetched until
 * the user drills in.
 */
export function DrillDownDialog({
  open,
  onClose,
  metricName,
  period,
  bucket,
  orgId,
  deviceIds,
  sessionIds,
}: DrillDownDialogProps) {
  const request =
    open && bucket
      ? bucketToQueryRequest({
          name: metricName,
          bucket,
          deviceIds,
          sessionIds,
        })
      : undefined;
  const {
    data,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQueryMetrics(request, orgId);

  const title = bucket
    ? getDrillDownTitle(
        // Display only — date-resolution title, so the lossy ns→number is fine.
        nsToIso(Number(bucket.start_time)),
        nsToIso(Number(bucket.end_time)),
        period,
      )
    : "";
  const records = data?.pages.flatMap((p) => p.items) ?? [];

  // Carry the dashboard's current time range + period onto the device metrics
  // tab (which renders the same MetricsDashboard and reads these params) so the
  // drill-down lands on a matching view. Period comes via prop; range lives in
  // the URL alongside it.
  const [searchParams] = useSearchParams();
  const deviceViewParams = new URLSearchParams({
    [DASHBOARD_PARAMS.range]: readRange(searchParams),
    [DASHBOARD_PARAMS.period]: period,
  }).toString();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <div className={styles.titleRow}>
          <span>
            {metricName}
            {title ? ` — ${title}` : ""}
          </span>
          <IconButton aria-label="close" size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers className={styles.content}>
        {isLoading && (
          <div className={styles.state}>
            <CircularProgress size={24} />
          </div>
        )}
        {error && (
          <Alert severity="error">
            Failed to load records: {error.message}
          </Alert>
        )}
        {!isLoading && !error && records.length === 0 && (
          <div className={styles.state}>No records in this window.</div>
        )}
        {!isLoading && !error && records.length > 0 && (
          <>
            <TableContainer className={styles.tableScroll}>
              <Table size="small" stickyHeader aria-label="metric records">
                <TableHead>
                  <TableRow>
                    <TableCell>Published</TableCell>
                    <TableCell align="right">Value</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Session</TableCell>
                    <TableCell>Invocation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((r, idx) => (
                    <TableRow key={`${r.session_id}-${idx}`}>
                      <TableCell>
                        {new Date(r.published).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">{r.value}</TableCell>
                      <TableCell>
                        {r.device_id ? (
                          // Land on the device's Metrics tab (not the default
                          // Datasets tab) — the user is drilling into metrics.
                          // Deliberately the BARE tab (no dashboard id): the
                          // active dashboard is org-scoped view state, and
                          // carrying it across would change behavior.
                          <RobotoLinkHoverUnderline
                            to={`${Goto.createDeviceMetricsRoute(r.device_id)}?${deviceViewParams}`}
                          >
                            {r.device_id}
                          </RobotoLinkHoverUnderline>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        {/* Land on the session's Metrics tab with this metric
                            preselected, so the tree opens expanded to it and
                            its detail card is shown. The "metrics" segment and
                            "selectedMetric" param are inlined rather than
                            imported from the Sessions feature (features must not
                            import from one another). */}
                        <RobotoLinkHoverUnderline
                          to={`${Goto.route.Sessions}/${r.session_id}/metrics?selectedMetric=${encodeURIComponent(
                            metricName,
                          )}`}
                        >
                          {r.session_id}
                        </RobotoLinkHoverUnderline>
                      </TableCell>
                      <TableCell>{r.invocation_id ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {hasNextPage && (
                <div className={styles.loadMore}>
                  <Button
                    variant="text"
                    size="small"
                    loading={isFetchingNextPage}
                    onClick={() => void fetchNextPage()}
                  >
                    Load more
                  </Button>
                </div>
              )}
            </TableContainer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
